import React, { useState, useEffect, useCallback } from 'react';
import { numbersApi, twilioApi, TrackingNumber } from '../services/api';

const Dialer: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState<TrackingNumber[]>([]);
  const [result, setResult] = useState<{ success: boolean; error?: string; to?: string } | null>(null);

  const fetchAvailableNumbers = useCallback(async () => {
    try {
      const response = await numbersApi.getNumbers();
      setAvailableNumbers(response.data.filter((n: TrackingNumber) => n.is_active));
    } catch (error) {
      console.error('Error fetching numbers:', error);
    }
  }, []);

  useEffect(() => { fetchAvailableNumbers(); }, [fetchAvailableNumbers]);

  const formatPhoneNumber = (value: string) => {
    const d = value.replace(/\D/g, '').slice(0, 10);
    if (d.length >= 7) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    if (d.length >= 4) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return d;
  };

  // Ten digits or nothing. The server validates too; this is just to stop the
  // button being usable with half a number typed.
  const digits = phoneNumber.replace(/\D/g, '');
  const canCall = digits.length === 10 && Boolean(fromNumber) && !isLoading;

  const selected = availableNumbers.find((n) => n.phone_number === fromNumber);

  const makeCall = async () => {
    if (!canCall) return;
    setIsLoading(true);
    setResult(null);
    try {
      const response = await twilioApi.makeCall({ to: `+1${digits}`, from: fromNumber });
      setResult({ success: true, to: response.data.to });
      setPhoneNumber('');
    } catch (error: any) {
      setResult({ success: false, error: error.response?.data?.error || 'Could not place the call.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dialer-page">
      <h1>Outbound Dialer</h1>

      <div className="dialer-card">
        <p className="dialer-explainer">
          This rings your phone first. Answer it, and you will be connected to the
          number below. The person you reach sees your tracking number.
        </p>

        <div className="form-row">
          <label htmlFor="fromNumber">Call from</label>
          <select
            id="fromNumber"
            value={fromNumber}
            onChange={(e) => setFromNumber(e.target.value)}
          >
            <option value="">Select a tracking number</option>
            {availableNumbers.map((n) => (
              <option key={n.id} value={n.phone_number}>
                {n.phone_number} — {n.campaign_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <label htmlFor="phoneNumber">Call to</label>
          <input
            id="phoneNumber"
            type="tel"
            value={phoneNumber}
            placeholder="(555) 123-4567"
            onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') makeCall(); }}
          />
        </div>

        {selected && (
          <p className="dialer-note">
            {selected.record_calls
              ? 'This call will be recorded, and the person you reach will be told so.'
              : 'This call will not be recorded.'}
          </p>
        )}

        <button className="dialer-button" onClick={makeCall} disabled={!canCall}>
          {isLoading ? 'Calling your phone…' : 'Call'}
        </button>

        {result && (
          <div className={result.success ? 'dialer-result ok' : 'dialer-result error'}>
            {result.success
              ? `Calling your phone now. Answer it to be connected to ${result.to}.`
              : result.error}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialer;
